import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { useWorkspaceService } from '@web/app/features/home/hooks';
import { getWorkspaceBadge, useCreateWorkspaceDialog } from '@web/app/shared';
import { Plus, RefreshCcw } from 'lucide-react';

import { WorkspaceAdvImage, WorkspaceBasicImage, WorkspaceImage, WorkspaceProImage } from '@codes/assets';
import { useOwnerStore } from '@codes/owner';
import { Button, SharedSpinner, Skeleton } from '@codes/ui-kit';

export const WorkspaceListPage = () => {
    const { myWorkspacesQueryResult, replaceWorkspace } = useWorkspaceService();
    const { owner } = useOwnerStore();

    const isLoading = myWorkspacesQueryResult.isFetching || myWorkspacesQueryResult.isRefetching;

    const { setWorkspaceDialog } = useCreateWorkspaceDialog({});
    const { t } = useTranslation();

    const navigate = useNavigate();
    const settingButtonTapped = (workspaceId: string | undefined) => {
        if (!workspaceId) {
            return;
        }
        navigate(`/w/${workspaceId}/settings`);
    };

    if (isLoading) {
        return (
            <div className="flex flex-col pt-8 px-[68px] max-md:p-5 w-full">
                <Skeleton className="w-44 h-10 mb-[26px]" />
                <Skeleton className="h-10 w-36 mb-6"></Skeleton>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] max-xs:grid-cols-1 gap-[26px] max-md:gap-[18px]">
                    <Skeleton className="h-[198px]" />
                    <Skeleton className="h-[198px]" />
                    <Skeleton className="h-[198px]" />
                    <Skeleton className="h-[198px]" />
                </div>
            </div>
        );
    }

    if (myWorkspacesQueryResult.error) {
        return (
            <div className="flex flex-col py-[34px] px-[68px] max-md:p-5 w-full">
                <div className="flex gap-x-2 flex-col items-center gap-y-4">
                    <div className="text-error">{t('workspace.error.fetchList')}</div>
                    <Button
                        className="text-sm p-2 h-auto"
                        onClick={() => myWorkspacesQueryResult.refetch()}
                        disabled={myWorkspacesQueryResult.isRefetching}
                    >
                        {t('workspace.error.retry')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col  w-full h-full pt-8 px-[68px] max-md:p-5">
            <Image className="max-w-[116px]" src={WorkspaceImage} />
            <div className="flex flex-wrap gap-y-2 items-center justify-between mt-[14px]">
                <div className="text-[22px] font-semibold">{t('workspace.list.myWorkspace')}</div>
                <div className="flex items-center gap-2">
                    <Button
                        className="h-11 pl-[19px] rounded-full gap-[6px]"
                        onClick={() => myWorkspacesQueryResult.refetch()}
                    >
                        {!myWorkspacesQueryResult.isRefetching ? (
                            <>
                                <RefreshCcw size={16} />
                                Refresh
                            </>
                        ) : (
                            <SharedSpinner className="w-4 h-4" />
                        )}
                    </Button>
                    <Button onClick={() => setWorkspaceDialog(true)} className="h-11 pl-[19px] rounded-full gap-[6px]">
                        <Plus size={16} /> {t('workspace.create.addWorkspace')}
                    </Button>
                </div>
            </div>
            <div className="w-full h-[1.5px] bg-gray-300 my-[38px]"></div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(325px,1fr))] max-xs:grid-cols-1 gap-[26px]">
                {myWorkspacesQueryResult.data?.data.map(workspace => {
                    return (
                        <Card
                            key={workspace.id}
                            className="cursor-pointer bg-gray-50"
                            onClick={() => replaceWorkspace(workspace.id as string, workspace.siteId as string)}
                        >
                            <div className="flex items-center justify-between gap-[9px] mb-[9px]">
                                <div className="truncate text-[19px] font-medium">
                                    {owner?.defWorkspaceId === workspace.id
                                        ? `${owner?.name} Workspace`
                                        : workspace.name}
                                </div>
                                {/*TODO: implement below*/}
                                {/* <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="p-[2px] group w-8 h-8">
                                            <EllipsisVertical className="w-4 h-4 group-hover:text-text transition-colors duration-200" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="center" className="w-[100px] p-1">
                                        <DropdownMenuItem
                                            onClick={e => {
                                                e.stopPropagation();
                                                settingButtonTapped(workspace.id);
                                            }}
                                        >
                                            Setting
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu> */}
                            </div>
                            <div className="flex items-center gap-2">
                                {getWorkspaceBadge(workspace.stereo)}
                                {/* <span className="text-gray-200">|</span>
                                    <span className="font-medium">1 Project</span> */}
                            </div>
                            <div className="flex justify-end">
                                {workspace.stereo === 'basic' && (
                                    <Image className="w-[92px]" src={WorkspaceBasicImage} />
                                )}
                                {workspace.stereo === 'pro' && <Image className="w-[92px]" src={WorkspaceProImage} />}
                                {workspace.stereo === 'ent' && <Image className="w-[92px]" src={WorkspaceAdvImage} />}
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};
